import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdvanceFilterOptionNew, FillerFieldType } from '../../helper';
import dayjs from 'dayjs';

const Wrapper = styled.div`
    display: flex;
    width: 100%;
    align-items: flex-start;
    align-content: flex-start;
    gap: 16px;
    flex-wrap: wrap;
    margin: 16px 0;
`;

const FastFilterContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const FastFilterLabel = styled.div`
    color: ${(props) => props.theme['Content/secondary']};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

const FastFilterPill = styled.div`
    display: flex;
    padding: 4px 16px;
    align-items: center;
    gap: 2px;
    border-radius: 64px;
    border: 1px solid ${(props) => props.theme['Borders/control']};
    background: ${(props) => props.theme['Surface/secondary']};
`;

const PillText = styled.div`
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    text-transform: capitalize;
`;

const ShowPill = styled.div`
    display: flex;
    padding: 4px 16px;
    align-items: center;
    gap: 2px;
    border-radius: 64px;
    border: 1px solid ${(props) => props.theme['Borders/control']};
    background: ${(props) => props.theme['Surface/secondary']};
    cursor: pointer;
    color: ${(props) => props.theme['Content/primary']};

    &:hover {
        background: ${(props) => props.theme['Surface/secondary']};
        border-color: ${(props) => props.theme['Borders/control']};

        .material-symbols-sharp {
            color: ${(props) => props.theme['Content/primary']} !important;
        }
    }
    &:active {
        background: ${(props) => props.theme['Surface/secondary']};
        border-color: ${(props) => props.theme['Borders/control']};
    }
`;

const ShowPillText = styled.div`
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

interface KeyValueObject {
    name: string;
    value: any;
    isMultiItems: boolean;
    type: string;
    option: AdvanceFilterOptionNew;
}

const transformObjectToArray = (obj: Record<string, any>, options: AdvanceFilterOptionNew[]) => {
    const result: KeyValueObject[] = [];
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (obj[key] !== '' && obj[key] !== null && obj[key]?.length !== 0) {
                const value = obj[key];
                const opt = options.find((o) => o.name === key);
                if (!opt) return;
                if (typeof value === 'string') {
                    result.push({
                        name: key,
                        value: [value],
                        isMultiItems: false,
                        type: key,
                        option: opt,
                    });
                } else if (Array.isArray(value)) {
                    if (opt.fieldType === FillerFieldType.tagMultiSelect) {
                        result.push({
                            name: key,
                            value: value,
                            isMultiItems: true,
                            type: key,
                            option: opt,
                        });
                    } else {
                        result.push({
                            name: key,
                            value: value,
                            isMultiItems: false,
                            type: key,
                            option: opt,
                        });
                    }
                } else {
                    result.push({
                        name: key,
                        value: value,
                        isMultiItems: false,
                        type: key,
                        option: opt,
                    });
                }
            }
        }
    }
    return result;
};

const returnRangeValue = (filter: any) => {
    if (filter.option.fieldType === FillerFieldType.dateRangePicker) {
        return `${dayjs.unix(+filter.value[0]).format('DD MMM YYYY')} - ${dayjs
            .unix(+filter.value[1])
            .format('DD MMM YYYY')}`;
    }
    if (filter.option.fieldType === FillerFieldType.amountRange) {
        return `${filter.value?.[0]} - ${filter.value?.[1]}`;
    }
    return '-';
};

interface FastFiltersProps {
    filters: any;
    removeFilter: any;
    options: AdvanceFilterOptionNew[];
}

const FILTERS_COUNTS_NUMBER_RESTRICTION = 7;

const FastFilters: React.FC<FastFiltersProps> = ({ filters, removeFilter, options }) => {
    const [fastFiltersArray, setFastFiltersArray] = useState<any[]>([]);
    const [isShowMore, setIsShowMore] = useState(false);

    useEffect(() => {
        const transformed = transformObjectToArray(filters, options);
        if (transformed) {
            setFastFiltersArray(transformed);
        }
    }, [filters]);

    const onShowButtonClickHandler = () => {
        setIsShowMore((prev) => !prev);
    };

    const returnValue = (filter: any, value: string) => {
        if (value === 'SDD') {
            value = 'SEPA DD';
        }
        if (filter?.option?.renderValueFunction) {
            return filter.option.renderValueFunction(value);
        }

        return value;
    };
    const findOptionByName = (name: string) => {
        // @ts-ignore
        return options?.find((o) => o.name === name);
    };
    return (
        <Wrapper>
            {fastFiltersArray.map((filter: KeyValueObject, index: number) => (
                <>
                    {!isShowMore && index < FILTERS_COUNTS_NUMBER_RESTRICTION && (
                        <FastFilterContainer key={index}>
                            <FastFilterLabel>
                                {findOptionByName(filter.name)?.label}
                            </FastFilterLabel>
                            {filter.option.fieldType === FillerFieldType.dateRangePicker ||
                            filter.option.fieldType === FillerFieldType.amountRange ? (
                                <ShowPill key={index}>
                                    <PillText
                                        data-test-id={`value-fast-filter-range-${filter.name}`}
                                    >
                                        {returnRangeValue(filter)}
                                    </PillText>
                                    <span
                                        style={{
                                            color: '#8E9091' /* TODO: need to change color. There is no such color in themes.ts */,
                                            cursor: 'pointer',
                                            fontSize: '20px',
                                        }}
                                        className="material-symbols-sharp"
                                        onClick={() => removeFilter(filter, filter.value)}
                                        data-test-id={`button-remove-fast-filter-${filter.name}`}
                                    >
                                        close
                                    </span>
                                </ShowPill>
                            ) : (
                                <>
                                    {filter.value.map((v: any, index: number) => (
                                        <FastFilterPill key={index}>
                                            <PillText
                                                data-test-id={`value-fast-filter-${filter.name}-${v}`}
                                            >
                                                {returnValue(filter, v)}
                                            </PillText>
                                            {!(
                                                findOptionByName(filter.name)?.name ===
                                                    'currency' && filters.amountRange.length > 0
                                            ) && (
                                                <span
                                                    style={{
                                                        color: '#8E9091' /* TODO: need to change color. There is no such color in themes.ts */,
                                                        cursor: 'pointer',
                                                        fontSize: '20px',
                                                    }}
                                                    className="material-symbols-sharp"
                                                    onClick={() => removeFilter(filter, v)}
                                                    data-test-id={`button-remove-fast-filter-${filter.name}-${v}`}
                                                >
                                                    close
                                                </span>
                                            )}
                                        </FastFilterPill>
                                    ))}
                                </>
                            )}
                        </FastFilterContainer>
                    )}
                    {isShowMore && (
                        <FastFilterContainer key={index}>
                            <FastFilterLabel>
                                {findOptionByName(filter.name)?.label}
                            </FastFilterLabel>
                            {filter.option.fieldType === FillerFieldType.dateRangePicker ||
                            filter.option.fieldType === FillerFieldType.amountRange ? (
                                <ShowPill key={index}>
                                    <PillText
                                        data-test-id={`value-fast-filter-range-more-${filter.name}`}
                                    >
                                        {returnRangeValue(filter)}
                                    </PillText>
                                    <span
                                        style={{
                                            color: '#8E9091' /* TODO: need to change color. There is no such color in themes.ts */,
                                            cursor: 'pointer',
                                            fontSize: '20px',
                                        }}
                                        className="material-symbols-sharp"
                                        onClick={() => removeFilter(filter, filter.value)}
                                        data-test-id={`button-remove-fast-filter-more-${filter.name}`}
                                    >
                                        close
                                    </span>
                                </ShowPill>
                            ) : (
                                <>
                                    {filter.value.map((v: any, index: number) => (
                                        <FastFilterPill key={index}>
                                            <PillText
                                                data-test-id={`value-fast-filter-more-${filter.name}-${v}`}
                                            >
                                                {returnValue(filter, v)}
                                            </PillText>
                                            <span
                                                style={{
                                                    color: '#8E9091' /* TODO: need to change color. There is no such color in themes.ts */,
                                                    cursor: 'pointer',
                                                    fontSize: '20px',
                                                }}
                                                className="material-symbols-sharp"
                                                onClick={() => removeFilter(filter, v)}
                                                data-test-id={`button-remove-fast-filter-more-${filter.name}-${v}`}
                                            >
                                                close
                                            </span>
                                        </FastFilterPill>
                                    ))}
                                </>
                            )}
                        </FastFilterContainer>
                    )}
                </>
            ))}
            {fastFiltersArray.length > FILTERS_COUNTS_NUMBER_RESTRICTION && (
                <ShowPill
                    onClick={onShowButtonClickHandler}
                    data-test-id="button-fast-filters-show-toggle"
                >
                    <ShowPillText>Show {isShowMore ? 'less' : 'more'}</ShowPillText>
                </ShowPill>
            )}
        </Wrapper>
    );
};

export default FastFilters;
